import { useRef } from "react";
import DatePicker from "react-datepicker";
import CalendarIcon from "./CalendarIcon";
import { XIcon } from "@heroicons/react/solid";
import "react-datepicker/dist/react-datepicker.css";

const DateRangePicker = ({ 
  label = "Date Range", 
  startDate, 
  endDate, 
  onChange, 
  className = "",
}) => {
  const datePickerRef = useRef(null);
  const containerRef = useRef(null);

  // Format the date range for display
  const formatDateRange = () => {
    if (startDate && endDate) {
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    } else if (startDate) {
      return `${formatDate(startDate)} - Select end date`;
    } else if (endDate) {
      return `Select start date - ${formatDate(endDate)}`;
    }
    return "Select date range";
  };

  // Format a single date
  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
  };

  // Handle date change from the picker
  const handleDateChange = (dates) => {
    const [start, end] = dates;
    onChange(start, end);
  };

  // Function to open the date picker when clicking on the container
  const handleContainerClick = () => {
    if (datePickerRef.current) {
      datePickerRef.current.setOpen(true);
    }
  };

  // Handle clearing the date range manually
  const handleClearClick = (e) => {
    e.stopPropagation();
    onChange(null, null);
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <p className="absolute -top-5 left-0 text-xs text-neutral">{label}</p>
      )}
      <div 
        ref={containerRef}
        className="flex items-center p-2 border border-gray2 rounded-md gap-2 hover:border-primary cursor-pointer transition-colors"
        onClick={handleContainerClick}
      >
        <CalendarIcon className="w-5 h-5 flex-shrink-0" />
        <div className="w-full flex-grow relative date-range-picker-container">
          <DatePicker
            ref={datePickerRef}
            selected={startDate}
            onChange={handleDateChange}
            startDate={startDate}
            endDate={endDate}
            selectsRange
            className="w-full outline-none bg-transparent"
            dateFormat="dd/MM/yyyy"
            placeholderText="Select date range"
            showMonthDropdown
            showYearDropdown
            isClearable={false} // Disable built-in clear button
            customInput={
              <input 
                value={formatDateRange()} 
                readOnly 
                className="w-full outline-none cursor-pointer text-sm pr-6"
              />
            }
          />
          {(startDate || endDate) && (
            <button 
              onClick={handleClearClick}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 p-1 text-gray hover:text-gray-dark z-10 date-range-clear-btn"
            >
              <XIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DateRangePicker;
